import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
// core
import {
  InputLabel, TextField, Button, Grid,
} from '@material-ui/core';

import useStyles from './styles';

import {
  SUPER_ADMIN,
  CLIENT,
  OWNER,
  SCOUT,
  CLIENT_EMPLOYEE,
  TELEAUDIT,
} from '../../utils/const/user';
import { editUser } from '../../requests/api/user';
import OutlinedSelect from '../../components/OutlinedSelect';
import Snackbar from '../../components/Snackbar';
import ImageUpload from '../../components/ImageUpload';

function UserInformation({
  userInfo, setUserInfo, userEdited, setUserEdited,
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('');

  // Edit user info
  // Add errors state near other state declarations
  const [errors, setErrors] = useState({});

  // Modify handleChangeUserInfo to validate password
  const handleChangeUserInfo = async (event) => {
    const { name, value } = event.target;

    // Clear previous error
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    // Validate password length if it's not empty
    if (name === 'password' && value && value.length < 8) {
      setErrors((prev) => ({
        ...prev,
        password: t('users.validation.passwordMin'),
      }));
    }

    if (name === 'active') {
      setUserInfo({
        ...userInfo,
        [name]: value === 'active',
      });
    } else {
      setUserInfo({
        ...userInfo,
        [name]: value,
      });
    }
  };
  // Back to the user table
  const handleBack = () => {
    setUserInfo(null);
  };

  // Saves the new active status and/or role
  const handleSave = async () => {
    // Separar base64 de URLs existentes
    const avatarBase64 = userInfo.avatar?.startsWith('data:')
      ? userInfo.avatar
      : null;
    const qrBase64 = userInfo.qrAuthorization?.startsWith('data:')
      ? userInfo.qrAuthorization
      : null;
    const avatarUrl = !userInfo.avatar?.startsWith('data:')
      ? userInfo.avatar
      : null;
    const qrUrl = !userInfo.qrAuthorization?.startsWith('data:')
      ? userInfo.qrAuthorization
      : null;

    const response = await editUser(
      userInfo.id,
      Boolean(userInfo.active),
      userInfo.role,
      userInfo.dni,
      userInfo.fullName,
      userInfo.password,
      userInfo.fileUrl,
      userInfo.phoneNumber,
      userInfo.lastName,
      avatarUrl,
      qrUrl,
      userInfo.bank,
      userInfo.numberAccount,
      avatarBase64,
      qrBase64,
    );
    if (response.success) {
      setSeverity('success');
      setMessage(t('users.userInfo.updateSuccess'));
      setUserEdited(!userEdited);
      setUserInfo(null);
    } else {
      setSeverity('error');
      setMessage(
        response.error
          ? response.error.errorMessage
          : t('users.userInfo.updateError'),
      );
    }
  };

  return (
    <>
      <div className={styles.userInfo} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Grid item xs={6}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <InputLabel>Avatar</InputLabel>
              <ImageUpload
                value={userInfo.avatar || ''}
                onChange={(url) => setUserInfo({ ...userInfo, avatar: url })}
                label="avatar"
              />
              <p className={styles.sizeMessage}>
                {t('users.userInfo.sizeMessage')}
              </p>
            </Grid>
            <Grid item xs={6}>
              <InputLabel>QR Autorización</InputLabel>
              <ImageUpload
                value={userInfo.qrAuthorization || ''}
                onChange={(url) => setUserInfo({ ...userInfo, qrAuthorization: url })
                }
                label="QR"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} style={{ marginTop: '16px' }}>
            <Grid item xs={12}>
              <InputLabel>{t('users.userInfo.name')}</InputLabel>
              <TextField
                name="fullName"
                value={userInfo.fullName}
                variant="outlined"
                fullWidth
                onChange={handleChangeUserInfo}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel>Apellido</InputLabel>
              <TextField
                name="lastName"
                value={userInfo.lastName || ''}
                variant="outlined"
                onChange={handleChangeUserInfo}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel>DNI</InputLabel>
              <TextField
                name="dni"
                value={userInfo.dni}
                variant="outlined"
                onChange={handleChangeUserInfo}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel>{t('users.userInfo.phone')}</InputLabel>
              <TextField
                name="phoneNumber"
                placeholder="Ejemplo: 56912345678"
                value={userInfo.phoneNumber}
                variant="outlined"
                type="number"
                onChange={handleChangeUserInfo}
                helperText="Ingrese un número de teléfono en formato internacional, omite el +, paréntesis y guiones."
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <InputLabel>Banco</InputLabel>
              <TextField
                name="bank"
                value={userInfo.bank || ''}
                variant="outlined"
                onChange={handleChangeUserInfo}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel>Número de Cuenta</InputLabel>
              <TextField
                name="numberAccount"
                value={userInfo.numberAccount || ''}
                variant="outlined"
                onChange={handleChangeUserInfo}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel>{t('users.userInfo.role')}</InputLabel>
              <OutlinedSelect
                name="role"
                items={[
                  { name: OWNER, value: OWNER },
                  { name: CLIENT, value: CLIENT },
                  { name: CLIENT_EMPLOYEE, value: CLIENT_EMPLOYEE },
                  { name: SUPER_ADMIN, value: SUPER_ADMIN },
                  { name: SCOUT, value: SCOUT },
                  { name: TELEAUDIT, value: TELEAUDIT },
                ]}
                value={userInfo.role}
                onChange={handleChangeUserInfo}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel>{t('users.userInfo.email')}</InputLabel>
              <TextField
                value={userInfo.email}
                variant="outlined"
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel>{t('users.userInfo.password')}</InputLabel>
              <TextField
                name="password"
                variant="outlined"
                fullWidth
                onChange={handleChangeUserInfo}
                error={!!errors.password}
                helperText={
                  errors.password || t('users.validation.passwordHelper')
                }
                type="password"
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel>{t('users.userInfo.status')}</InputLabel>
              <OutlinedSelect
                name="active"
                items={[
                  { name: t('users.userInfo.active'), value: 'active' },
                  { name: t('users.userInfo.inactive'), value: 'inactive' },
                  // { name: t('users.userInfo.blacklist'), value: 'blacklist' },
                ]}
                value={userInfo.active ? 'active' : 'inactive'}
                onChange={handleChangeUserInfo}
                fullWidth
              />
            </Grid>
            <Grid className={styles.buttonsContainer} xs={12}>
              <Button onClick={handleBack}>
                {t('users.userInfo.buttons.back')}
              </Button>
              <Button onClick={handleSave}>
                {t('users.userInfo.buttons.save')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <Snackbar
        open={!!message}
        message={message}
        severity={severity}
        onClose={() => setMessage('')}
      />
    </>
  );
}

export default UserInformation;
